import { useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { SizedRelativeSpinner } from '@components/loading-spinner';
import { SearchBox } from '@components/search-box';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { EditableCellPopover, OptionEditorBody } from './editable-cell-popover';
import { editableCellLocale } from './locale';
import { applySelectAll, getSelectAllState, isSameSelection } from './option-selection';
import { EditableCellEditorProps, MultiSelectCellValue } from './types';
import { useEditOptions } from './use-edit-options';
import { useEditableCellTrigger } from './use-editable-cell-trigger';
import { useOptionSearch } from './use-option-search';

import { typeRenderMap } from '../renderers';

type EditableMultiSelectCellProps = EditableCellEditorProps<MultiSelectCellValue | undefined>;

export const EditableMultiSelectCell = ({ column, row, value }: EditableMultiSelectCellProps) => {
  const { t } = useScopedTranslate(editableCellLocale);
  const getTrigger = useEditableCellTrigger();
  const { options, isLoading, hasError, load } = useEditOptions(column.getEditOptions);
  const search = useOptionSearch(options);
  const { filteredOptions, hasActiveSearch } = search;
  const [draft, setDraft] = useState<Array<string>>([]);

  // Select all is about the whole list, so it steps aside while a search has
  // the list narrowed - which is also what makes "all of these" versus "all of
  // them" a question that can't come up.
  const selectableValues = options.map((option) => option.value);

  // Neither control has a list to act on until the options land: a search box
  // would filter nothing, and Select all would read the empty list as nothing
  // selected and then animate its tick the moment the options arrive. Waiting
  // is also what the single-select and user editors do, since the menu list
  // returns its loading row before it renders either.
  const isShowingOptions = !isLoading && !hasError;
  const selectAllState = getSelectAllState({ selected: draft, values: selectableValues });

  const currentShortIds = Array.isArray(value) ? value.map((option) => option.shortId) : [];

  const trigger = getTrigger(value, column, row, () =>
    typeRenderMap.multi_select(value, column, row),
  );

  return (
    <EditableCellPopover
      trigger={trigger}
      column={column}
      row={row}
      value={value}
      onOpen={() => {
        load();
        search.reset();
        setDraft(currentShortIds);
      }}
      isSaveOnEnter={false}
      // Save writes the draft, so it stays inactive while the draft cannot have
      // moved: no options to tick yet, or every tick back where it started.
      isSaveDisabled={isLoading || isSameSelection(draft, currentShortIds)}
      onSave={async () => {
        await column.onEdit?.(row, draft);
      }}
    >
      <OptionEditorBody>
        <Stack space={2}>
          {isShowingOptions && (
            <SearchBox
              placeholder={t('searchPlaceholder')}
              value={search.search}
              onChange={search.onSearchChange}
              stopPropagation
              shouldBlurOnEscape={false}
            />
          )}
          {isShowingOptions && !hasActiveSearch && (
            <Checkbox
              label={t('selectAll')}
              labelFont="semibold14"
              value={selectAllState}
              // A dash means "some of these", and the label promises all of
              // them - so both unchecked and mixed tick the whole list, and
              // only a full tick clears it. The checkbox reads its own mixed
              // state as false, so the intent is decided here.
              onChange={() =>
                setDraft((selected) =>
                  applySelectAll({
                    selected,
                    values: selectableValues,
                    isSelected: selectAllState !== true,
                  }),
                )
              }
            />
          )}
          <OptionList>
            {isLoading ? (
              <Stack orientation="horizontal" space={2} align="center">
                <SizedRelativeSpinner size={16} $skipMargin $borderWidth={2} />
                <Text font="regular14" color="textHelper" text={t('loadingOptions')} />
              </Stack>
            ) : hasError ? (
              <Button variant="ghost" text={t('loadOptionsError')} onClick={load} />
            ) : filteredOptions.length === 0 ? (
              // Centred, the way the designs draw an empty search.
              <Text font="regular14" color="textHelper" text={t('noResults')} center />
            ) : (
              filteredOptions.map((option) => (
                <Checkbox
                  key={option.value}
                  label={option.label}
                  value={draft.includes(option.value)}
                  onChange={(checked) =>
                    setDraft((current) =>
                      checked
                        ? current.concat(option.value)
                        : current.filter((item) => item !== option.value),
                    )
                  }
                />
              ))
            )}
          </OptionList>
        </Stack>
      </OptionEditorBody>
    </EditableCellPopover>
  );
};

const OptionList = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: pxToRem(8),
  maxHeight: pxToRem(280),
  overflowY: 'auto',
});
